import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  OfferSortBy,
  OfferSortByDirection,
  ProductFiltersT,
  ProductT,
  SkuT,
} from '@lambda/apis/src/catalog-v2/types';
import { isEmpty } from 'lodash-es';
import { PRODUCTS_PER_PAGE } from '@lambda/apis/src/catalog-v2/constants';
import {
  CollectionPageProps,
  FilterType,
  SelectedFilters,
  useCollectionPageResult,
} from './types';
import { logger } from '@/lib/logger';
import i18n from '@/settings/i18n';
import {
  buildParams,
  initializeFiltersFromSearchParams,
  stripHiddenFilterValues,
  toggleFilterLogic,
  toggleVariantLogic,
  updatePriceRange,
} from './helpers';

const t = i18n.init('collections');

// Query params owned by this hook — rebuilt from state on every URL update.
// `handle`/`pk` are route segments, the rest are filter/sort/page state.
const MANAGED_QUERY_KEYS = new Set([
  'handle',
  'pk',
  'categoryIds',
  'brandIds',
  'minPrice',
  'maxPrice',
  'sortBy',
  'sortDirection',
  'page',
]);

const useCollectionPage = (
  value: CollectionPageProps,
): useCollectionPageResult => {
  const router = useRouter();
  const abortControllerRef = useRef<AbortController | null>(null);

  const collectionName =
    value.type === 'MS_CATALOG_BRAND_COLLECTION_PAGE'
      ? t`${{ brand: value.collectionName }} Products`
      : value.collectionName;

  // --- Initial filters + sort
  const { filters: initialFilters, sort: initialSort } = useMemo(
    () => initializeFiltersFromSearchParams(value.searchParams, value.filters),
    [value.searchParams, value.filters],
  );

  // --- Default price range
  const defaultPriceRange =
    value?.filters?.priceRange != null
      ? {
          min: value.filters.priceRange.min,
          max: value.filters.priceRange.max,
        }
      : undefined;

  // --- State
  const [products, setProducts] = useState<ProductT[] | SkuT[]>(value.products);
  const [filters, setFilters] = useState<ProductFiltersT>(
    stripHiddenFilterValues(value.filters),
  );
  const [lastSelectedFilter, setLastSelectedFilter] = useState<string | null>(
    null,
  );

  const [total, setTotal] = useState(value.total);
  // Initialise from the URL so it matches the page rendered server-side.
  const [currentPage, setCurrentPage] = useState(() => {
    const raw = Array.isArray(router.query.page)
      ? router.query.page[0]
      : router.query.page;
    const page = Math.floor(Number(raw));

    return Number.isFinite(page) && page > 1 ? page : 1;
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [shouldApplyFilters, setShouldApplyFilters] = useState(false);

  const [selectedFilters, setSelectedFilters] =
    useState<SelectedFilters | null>(initialFilters);

  const [sortByFilter, setSortByFilter] = useState({
    sortBy: initialSort.sortBy,
    sortDirection: initialSort.sortDirection,
  });

  // --- Build the canonical collection URL for the given filters/sort/page.
  const buildUrl = useCallback(
    (
      _filters: SelectedFilters,
      sort: { sortBy: OfferSortBy; sortDirection: OfferSortByDirection },
      page = 1,
    ) => {
      const basePath = router.asPath.split('?')[0];

      // Strip the params this hook fully manages (route segments + filters/
      // sort/page) so they're rebuilt purely from state — otherwise cleared
      // filters and the route's `pk`/`handle` would linger in the URL. Anything
      // else (utm_*, gclid, …) is preserved as a tracking param.
      const trackingQuery = Object.fromEntries(
        Object.entries(router.query).filter(
          ([key]) =>
            !MANAGED_QUERY_KEYS.has(key) && !key.startsWith('variants['),
        ),
      );

      const urlParams = buildParams({
        selectedFilters: _filters,
        fixedFilters: {},
        sort,
        page,
        defaultPriceRange,
        routerQuery: trackingQuery,
      })
        .toString()
        .replace(/%5B/g, '[')
        .replace(/%5D/g, ']');

      return `${basePath}${urlParams ? `?${urlParams}` : ''}`;
    },
    [router.asPath, router.query, defaultPriceRange],
  );

  // Crawlable href for a given page — used by the pagination links.
  const getPageHref = useCallback(
    (page: number) =>
      buildUrl(
        selectedFilters ?? { categories: [], brands: [], variants: {} },
        sortByFilter,
        page,
      ),
    [buildUrl, selectedFilters, sortByFilter],
  );

  // --- Update URL
  const updateUrl = useCallback(
    async (
      _filters: SelectedFilters,
      sort: { sortBy: OfferSortBy; sortDirection: OfferSortByDirection },
      page = 1,
    ) => {
      const newUrl = buildUrl(_filters, sort, page);

      // avoid re-renders
      if (newUrl !== router.asPath) {
        await router.replace(newUrl, undefined, { shallow: true });
      }
    },
    [buildUrl, router.asPath], // don't depend on full router object
  );

  // --- Refresh Filters Logic
  const refreshFilters = (updatedFilters: ProductFiltersT): ProductFiltersT => {
    const finalFilters: ProductFiltersT = {
      ...updatedFilters,
      priceRange: value.filters.priceRange, // always keep original price range
      categories: value.filters.categories, // always keep original categories
    };

    // --- Preserve brands if last filter was a brand
    if (lastSelectedFilter === 'brands') {
      finalFilters.brands = filters.brands;
    }

    // --- Preserve the specific variant group if applicable
    if (lastSelectedFilter?.startsWith('var-')) {
      const preservedVariant = filters.variants?.find(
        (v) => v.id === lastSelectedFilter,
      );

      if (preservedVariant) {
        if (
          Array.isArray(updatedFilters.variants) &&
          updatedFilters.variants.length > 0
        ) {
          finalFilters.variants = updatedFilters.variants.map((v) =>
            v.id === lastSelectedFilter ? preservedVariant : v,
          );
        } else {
          finalFilters.variants = [preservedVariant];
        }
      }
    }

    if (isEmpty(selectedFilters?.brands)) {
      finalFilters.brands = value.filters.brands;
    }

    // Always preserve selected filters (categories, brands, variants)
    if (selectedFilters) {
      // --- Preserve selected categories
      selectedFilters.categories.forEach((sel) => {
        const exists = finalFilters.categories?.some((c) => c.id === sel.id);

        if (!exists) {
          finalFilters.categories?.push(sel);
        }
      });

      // --- Preserve selected brands
      selectedFilters.brands.forEach((sel) => {
        const exists = finalFilters.brands?.some((b) => b.id === sel.id);

        if (!exists) {
          finalFilters.brands?.push(sel);
        }
      });

      // --- Preserve selected variant values
      Object.entries(selectedFilters.variants).forEach(
        ([variantId, selectedValues]) => {
          const selectedValueObjs = selectedValues.map((v) => ({ value: v }));
          const variant = finalFilters.variants?.find(
            (v) => v.id === variantId,
          );

          if (!variant) {
            // backend removed entire variant → add it back
            const original = value.filters.variants?.find(
              (v) => v.id === variantId,
            );

            finalFilters.variants?.push({
              id: variantId,
              name: original?.name ?? '',
              values: selectedValueObjs,
            });
          } else {
            // merge missing selected values
            selectedValueObjs.forEach((sv) => {
              if (!variant.values.some((v) => v.value === sv.value)) {
                variant.values.push(sv);
              }
            });
          }
        },
      );
    }

    return finalFilters;
  };

  // --- Apply Filters
  const applyFilters = useCallback(
    async (
      _filters: SelectedFilters,
      sort: { sortBy: OfferSortBy; sortDirection: OfferSortByDirection },
    ) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();

      abortControllerRef.current = controller;

      try {
        setIsApplyingFilters(true);

        const searchParams = buildParams({
          selectedFilters: _filters,
          fixedFilters: value.fixedFilters,
          sort,
          limit: PRODUCTS_PER_PAGE,
        }).toString();

        const res =
          value.type === 'MS_CATALOG_DEALS'
            ? await fetch(`/api/catalog-v2/deals?${searchParams}`, {
                signal: controller.signal,
              })
            : await fetch(`/api/catalog-v2/products?${searchParams}`, {
                signal: controller.signal,
              });
        const data = await res.json();

        if ('items' in data) {
          setIsApplyingFilters(false);
          setProducts(data.items);
          setTotal(data.total);
          // Filters changed — reset to the first page.
          setCurrentPage(1);

          const refreshedFilters = refreshFilters(data.filters);

          setFilters(stripHiddenFilterValues(refreshedFilters));
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          logger.debug('Previous fetch aborted');
        } else {
          setIsApplyingFilters(false);
          logger.error({ err }, 'Failed to update collection data');
        }
      }
    },
    [value.fixedFilters, filters, lastSelectedFilter],
  );

  // NOTE: Merging the below two useEffects into one causes the applyFilters to be
  // called twice on filter toggle. Keeping them separate to avoid that.

  // Update URL (no fetch). On mount, preserve the page from the URL; once the
  // user changes a filter/sort, reset to page 1. Pagination itself is written
  // by `goToPage`, so this effect intentionally doesn't depend on currentPage.
  useEffect(() => {
    if (!router.isReady || !selectedFilters) {
      return;
    }
    updateUrl(
      selectedFilters,
      sortByFilter,
      shouldApplyFilters ? 1 : currentPage,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, sortByFilter, router.isReady, shouldApplyFilters]);

  // Apply filters (fetch data)
  useEffect(() => {
    if (!shouldApplyFilters || !selectedFilters) {
      return;
    }
    applyFilters(selectedFilters, sortByFilter);
  }, [shouldApplyFilters, selectedFilters, sortByFilter]);

  // --- Filter + Sort handlers
  const toggleFilter = useCallback(
    (type: FilterType, id: string, name: string) => {
      if (!shouldApplyFilters) {
        setShouldApplyFilters(true);
      }

      setSelectedFilters((prev) => {
        if (!prev) {
          return prev;
        }

        // Compute the new selected filters state
        const newSelectedFilters = toggleFilterLogic(prev, type, id, name);

        // Determine if this filter type still has any selections
        let hasActiveSelection = false;

        if (type === FilterType.CATEGORY) {
          hasActiveSelection = newSelectedFilters.categories.length > 0;
        }

        if (type === FilterType.BRAND) {
          hasActiveSelection = newSelectedFilters.brands.length > 0;
        }

        // Update lastSelectedFilter only if there’s at least one active filter
        if (hasActiveSelection) {
          setLastSelectedFilter(type);
        }

        logger.debug(
          { newSelectedFilters },
          'Toggled filter, updated selected filters',
        );

        return newSelectedFilters;
      });
    },
    [shouldApplyFilters],
  );

  const toggleVariantFilter = useCallback(
    (variantId: string, _value: string) => {
      if (!shouldApplyFilters) {
        setShouldApplyFilters(true);
      }

      setSelectedFilters((prev) => {
        if (!prev) {
          return prev;
        }

        // Apply the toggle logic first
        const newSelectedFilters = toggleVariantLogic(prev, variantId, _value);

        // Check if this variant group still has selections
        const hasActiveSelection =
          Array.isArray(newSelectedFilters.variants?.[variantId]) &&
          newSelectedFilters.variants[variantId].length > 0;

        // Update lastSelectedFilter only if something is still selected
        if (hasActiveSelection) {
          setLastSelectedFilter(variantId);
        }

        return newSelectedFilters;
      });
    },
    [shouldApplyFilters],
  );

  const onPriceRangeChange = useCallback(
    (min: number, max: number) => {
      if (!defaultPriceRange) {
        return;
      }

      if (!shouldApplyFilters) {
        setShouldApplyFilters(true);
      }
      setSelectedFilters((prev) =>
        prev ? updatePriceRange(prev, min, max, defaultPriceRange) : prev,
      );
    },
    [defaultPriceRange, shouldApplyFilters],
  );

  const onSortByChange = useCallback(
    (sortBy: OfferSortBy, sortDirection: OfferSortByDirection) => {
      if (!shouldApplyFilters) {
        setShouldApplyFilters(true);
      }
      setSortByFilter({ sortBy, sortDirection });
    },
    [shouldApplyFilters],
  );

  // --- Pagination
  const goToPage = useCallback(
    async (page: number) => {
      if (isLoadingMore || !selectedFilters || page === currentPage) {
        return;
      }
      setIsLoadingMore(true);

      try {
        const params = buildParams({
          selectedFilters,
          fixedFilters: value.fixedFilters,
          sort: sortByFilter,
          offset: (page - 1) * PRODUCTS_PER_PAGE,
          limit: PRODUCTS_PER_PAGE,
        });

        const res =
          value.type === 'MS_CATALOG_DEALS'
            ? await fetch(`/api/catalog-v2/deals?${params.toString()}`)
            : await fetch(`/api/catalog-v2/products?${params.toString()}`);
        const data = await res.json();

        if ('items' in data) {
          // Classic pagination — replace the list rather than append.
          setProducts(data.items);
          setTotal(data.total);
          setCurrentPage(page);
          updateUrl(selectedFilters, sortByFilter, page);

          if (typeof window !== 'undefined') {
            const top = document.getElementById('collection-page');

            if (top) {
              // Offset by the sticky navbar so the top isn't hidden under it.
              const navHeight =
                document.getElementById('header')?.offsetHeight ?? 0;
              const y =
                top.getBoundingClientRect().top +
                window.scrollY -
                navHeight -
                20; // Padding for visual comfort

              window.scrollTo({ top: y, behavior: 'smooth' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
        }
      } catch (err) {
        logger.error({ error: err }, 'Failed to fetch page');
      } finally {
        setIsLoadingMore(false);
      }
    },
    [
      currentPage,
      isLoadingMore,
      selectedFilters,
      sortByFilter,
      value.fixedFilters,
      updateUrl,
    ],
  );

  const clearCategoryFilters = useCallback(() => {
    if (!selectedFilters) {
      return;
    }

    if (!shouldApplyFilters) {
      setShouldApplyFilters(true);
    }
    setSelectedFilters({
      ...selectedFilters,
      categories: [],
    });
  }, [selectedFilters, shouldApplyFilters]);

  const clearFilters = useCallback(() => {
    if (!selectedFilters) {
      return;
    }

    if (!shouldApplyFilters) {
      setShouldApplyFilters(true);
    }
    setSelectedFilters({
      categories: [],
      brands: [],
      variants: {},
      priceRange: undefined,
    });
  }, [selectedFilters, shouldApplyFilters]);

  // Deals
  if (value.type === 'MS_CATALOG_DEALS') {
    return {
      ...value,
      collectionName,
      products: products as SkuT[],
      filters,
      categoryName: 'Smartphones',
      categoryWarrantyInfo: null,
      selectedFilters,
      toggleFilter,
      toggleVariantFilter,
      clearFilters,
      clearCategoryFilters,
      onPriceRangeChange,
      sortByFilter,
      onSortByChange,
      goToPage,
      getPageHref,
      total,
      currentPage,
      pageCount: Math.ceil(total / PRODUCTS_PER_PAGE),
      isLoadingMore,
      isApplyingFilters,
    };
  }

  // Catalog
  return {
    ...value,
    collectionName,
    products: products as ProductT[],
    filters,
    categoryName: 'Smartphones',
    categoryWarrantyInfo: null,
    selectedFilters,
    toggleFilter,
    toggleVariantFilter,
    clearFilters,
    clearCategoryFilters,
    onPriceRangeChange,
    sortByFilter,
    onSortByChange,
    goToPage,
    getPageHref,
    total,
    currentPage,
    pageCount: Math.ceil(total / PRODUCTS_PER_PAGE),
    isLoadingMore,
    isApplyingFilters,
  };
};

export default useCollectionPage;
